/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, Hidden, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon, CheckIcon } from '@coursera/cds-icons';

import { numberOfProducts } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import BLUE_C_LOGO from 'images/articles/coursera-c-logo.svg';
import C_PLUS_BG from 'images/articles/coursera-plus-promo-bg-mobile.svg';
import C_PLUS_LOGO from 'images/articles/coursera-plus-white.png';

import _t from 'i18n!nls/articles';

const styles = {
  banner: () => css`
    background: url(${BLUE_C_LOGO}) -40% 50% no-repeat,
      linear-gradient(
        to right,
        var(--cds-color-blue-800) 0%,
        var(--cds-color-blue-700) 52.6%,
        var(--cds-color-blue-600) 100%
      );
    border-radius: var(--cds-spacing-50);
    margin: var(--cds-spacing-300) auto;
    max-width: 750px;
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
    position: relative;
    align-items: center;
    min-height: 300px;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-400);
      background: url(${C_PLUS_BG}) no-repeat,
        linear-gradient(
          to right,
          var(--cds-color-blue-800) 0%,
          var(--cds-color-blue-700) 52.6%,
          var(--cds-color-blue-600) 100%
        );
      background-size: cover;
    }
  `,
  header: () => css`
    display: flex;
    flex-direction: column;
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  title: () => css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-150) 0 var(--cds-spacing-200);
    }
  `,
  logoWrapper: () => css`
    display: block;
    line-height: 0;

    img {
      margin-top: 0 !important; /* overwritten by the richtext rules */
      width: auto;
      max-width: 104px;
    }
  `,
  listContainer: () => css`
    &:not(.rc-RichText.rc-RichText) {
      list-style-type: none;
      margin: 0;
      padding: 0;

      li {
        display: inline-flex;
        margin: 0 0 0 var(--cds-spacing-200);

        &:not(:last-child) {
          margin-bottom: var(--cds-spacing-200);
        }

        svg {
          align-self: flex-start;
          margin-right: var(--cds-spacing-150);
          min-width: 20px;
        }
      }
    }
    ${breakpoints.down('xs')} {
      max-width: 100%;

      &:not(.rc-RichText.rc-RichText) {
        margin: 0;

        li {
          display: inline-flex;
          margin: 0;

          &:not(:last-child) {
            margin-bottom: var(--cds-spacing-150);
          }
        }
      }
    }
  `,
  ctaSm: () => css`
    margin-top: var(--cds-spacing-300);
  `,
};

const CPlusPromoUnit = () => {
  const header = _t('Build job-ready skills with a Coursera Plus subscription');
  const ctaText = _t('Start 7-day free trial');
  const bullets = [
    <FormattedMessage
      message={_t(
        '{highlightedText} from world-class universities and companies, including Google, Yale, Salesforce, and more'
      )}
      highlightedText={
        <strong>{_t('Get access to #{numberOfProducts} learning programs', { numberOfProducts })}</strong>
      }
      key="value-props-1"
    />,
    <FormattedMessage
      message={_t('{highlightedText} and find your best fit at no additional cost')}
      highlightedText={<strong>{_t('Try different courses')}</strong>}
      key="value-props-2"
    />,
    <FormattedMessage
      message={_t('{highlightedText} for learning programs you complete')}
      highlightedText={<strong>{_t('Earn certificates')}</strong>}
      key="value-props-3"
    />,
    <FormattedMessage
      message={_t('{highlightedText} cancel anytime')}
      highlightedText={
        <strong>{_t('A subscription price of $#{subscriptionPrice}/month,', { subscriptionPrice: 59 })}</strong>
      }
      key="value-props-4"
    />,
  ];

  const renderCTA = (isFullWidth: boolean) => (
    <Button
      variant="primaryInvert"
      icon={<ArrowNextIcon />}
      fullWidth={isFullWidth}
      component={TrackedLink2}
      trackingName="article_c_plus_promo_unit"
      withVisibilityTracking
      href="/courseraplus?trk_ref=articlesCPlusPromoUnit"
      aria-label={ctaText}
      edgeAlign="start"
    >
      {ctaText}
    </Button>
  );

  return (
    <Grid container justifyContent="space-between" css={styles.banner}>
      <Grid item sm={6} css={styles.header}>
        <figure css={styles.logoWrapper}>
          <Imgix src={C_PLUS_LOGO} alt={_t('Coursera Plus')} maxWidth={104} maxHeight={10} />
        </figure>
        <Typography color="invertBody" variant="h1semibold" component="h5" css={styles.title}>
          {header}
        </Typography>
        <Hidden xsDown={true}>{renderCTA(false)} </Hidden>
      </Grid>
      <Grid item sm={6}>
        <Grid css={styles.listContainer} component="ul" container>
          {bullets.map((bullet) => (
            <Grid component="li" item sm={12} key={`value-props-card-tip-${bullet}`}>
              <CheckIcon size="medium" color="invert" />
              <Typography2 variant="bodySecondary" component="span" color="invertBody">
                {bullet}
              </Typography2>
            </Grid>
          ))}
        </Grid>
        <Hidden smUp={true} css={styles.ctaSm}>
          {renderCTA(true)}
        </Hidden>
      </Grid>
    </Grid>
  );
};

export default CPlusPromoUnit;
